import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, Inbox, Wrench, BookOpen, UserCog, Mail } from "lucide-react";
import { useAdminAuth } from "@/admin/AdminAuthContext";

/**
 * Shell around every protected admin page:
 *  - thin top bar with the SAS mark, current admin email, and logout
 *  - slim left rail for future sections (Tools / Articles / Workshops)
 *  - main content area rendered via <Outlet />
 *
 * Deliberately minimal — we are not styling a full CMS yet.
 */
export default function AdminLayout() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" dir="ltr" lang="en">
      {/* Top bar */}
      <header
        className="border-b border-[color:var(--sas-line)] bg-white sticky top-0 z-10"
        data-testid="admin-topbar"
      >
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/sas-logo.svg" alt="SAS" className="h-7 w-7" />
            <div className="leading-tight">
              <div className="text-[13px] font-semibold text-[color:var(--sas-ink)]">SAS Admin</div>
              <div className="text-[11px] text-[color:var(--sas-ink-2)]">
                Website management console
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {admin?.email && (
              <span
                className="text-xs text-[color:var(--sas-ink-2)] hidden sm:inline"
                data-testid="admin-current-email"
              >
                {admin.email}
              </span>
            )}
            <button
              type="button"
              onClick={onLogout}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[color:var(--sas-ink)] px-3 py-1.5 rounded-full border border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)] transition-colors"
              data-testid="admin-logout"
            >
              <LogOut size={14} aria-hidden />
              Log out
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 w-full max-w-6xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-8">
        {/* Side nav — sections are placeholders for now (no routes yet) */}
        <aside className="md:sticky md:top-20 md:self-start" data-testid="admin-sidenav">
          <NavLink
            to="/admin"
            end
            className={({ isActive }) =>
              `flex items-center gap-2 text-sm px-3 py-2 rounded-lg border ${
                isActive
                  ? "border-[color:var(--sas-line)] bg-[color:var(--sas-accent-soft)]/60 text-[color:var(--sas-ink)]"
                  : "border-transparent text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)]"
              }`
            }
          >
            <LayoutDashboard size={15} aria-hidden />
            Dashboard
          </NavLink>

          <NavLink
            to="/admin/requests"
            className={({ isActive }) =>
              `mt-1 flex items-center gap-2 text-sm px-3 py-2 rounded-lg border ${
                isActive
                  ? "border-[color:var(--sas-line)] bg-[color:var(--sas-accent-soft)]/60 text-[color:var(--sas-ink)]"
                  : "border-transparent text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)]"
              }`
            }
            data-testid="admin-nav-requests"
          >
            <Inbox size={15} aria-hidden />
            Requests
          </NavLink>

          <NavLink
            to="/admin/tools"
            className={({ isActive }) =>
              `mt-1 flex items-center gap-2 text-sm px-3 py-2 rounded-lg border ${
                isActive
                  ? "border-[color:var(--sas-line)] bg-[color:var(--sas-accent-soft)]/60 text-[color:var(--sas-ink)]"
                  : "border-transparent text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)]"
              }`
            }
            data-testid="admin-nav-tools"
          >
            <Wrench size={15} aria-hidden />
            Tools
          </NavLink>

          <NavLink
            to="/admin/articles"
            className={({ isActive }) =>
              `mt-1 flex items-center gap-2 text-sm px-3 py-2 rounded-lg border ${
                isActive
                  ? "border-[color:var(--sas-line)] bg-[color:var(--sas-accent-soft)]/60 text-[color:var(--sas-ink)]"
                  : "border-transparent text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)]"
              }`
            }
            data-testid="admin-nav-articles"
          >
            <BookOpen size={15} aria-hidden />
            Articles
          </NavLink>

          <NavLink
            to="/admin/newsletter"
            className={({ isActive }) =>
              `mt-1 flex items-center gap-2 text-sm px-3 py-2 rounded-lg border ${
                isActive
                  ? "border-[color:var(--sas-line)] bg-[color:var(--sas-accent-soft)]/60 text-[color:var(--sas-ink)]"
                  : "border-transparent text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)]"
              }`
            }
            data-testid="admin-nav-newsletter"
          >
            <Mail size={15} aria-hidden />
            Newsletter
          </NavLink>

          <NavLink
            to="/admin/account"
            className={({ isActive }) =>
              `mt-1 flex items-center gap-2 text-sm px-3 py-2 rounded-lg border ${
                isActive
                  ? "border-[color:var(--sas-line)] bg-[color:var(--sas-accent-soft)]/60 text-[color:var(--sas-ink)]"
                  : "border-transparent text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)]"
              }`
            }
            data-testid="admin-nav-account"
          >
            <UserCog size={15} aria-hidden />
            Account
          </NavLink>

          <div className="mt-6 text-[11px] font-semibold tracking-wide uppercase text-[color:var(--sas-ink-2)] px-3">
            Coming later
          </div>
          <ul className="mt-2 space-y-1">
            {["Workshops"].map((label) => (
              <li key={label}>
                <span
                  className="flex items-center justify-between gap-2 text-sm px-3 py-2 rounded-lg text-[color:var(--sas-ink-2)] cursor-not-allowed"
                  data-testid={`admin-nav-placeholder-${label.toLowerCase()}`}
                  aria-disabled="true"
                >
                  {label}
                  <span className="text-[10px] uppercase tracking-wide text-[color:var(--sas-ink-2)]">
                    Soon
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </aside>

        <main className="min-w-0">
          <Outlet />
        </main>
      </div>

      <footer className="border-t border-[color:var(--sas-line)]">
        <div className="max-w-6xl mx-auto px-6 py-4 text-[11px] text-[color:var(--sas-ink-2)] flex items-center justify-between">
          <span>Syrian AI Society · Admin Console</span>
          <span>Session is valid for 7 days from sign-in.</span>
        </div>
      </footer>
    </div>
  );
}
